import { CollectionStatusEnum } from "@kanpla/api-contract";
import { Link } from "react-router-dom";

import {
  type TransformedCollectOrder,
  useGetCollectOrder
} from "~/api/collect/hooks/use-get-collect-order";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import {
  CollectSummary,
  type CollectSummaryOrderLine
} from "~/components/collect/collect-summary";
import { useOrderLinesToCollect } from "~/providers/store/collect/hooks";
import { type CollectStore } from "~/providers/store/collect/store.types";
import { useEmployee } from "~/providers/store/employee";

export const CollectBasket = () => {
  const employeeData = useEmployee();

  const { data: userProducts } = useUserProductsPos();
  const childId = userProducts?.user?.childId;
  const groupName = userProducts?.user?.groupName || employeeData.groupName;

  const orderLinesToCollect = useOrderLinesToCollect();

  const { data: order, status } = useGetCollectOrder({
    childId
  });

  if (status === "pending") {
    return (
      <div className="p-4">
        <CollectSummary.Skeleton />
      </div>
    );
  }

  if (
    status === "error" ||
    !order ||
    order.collectionStatus === CollectionStatusEnum.NO_ORDERS
  ) {
    return null;
  }

  const activeOrderLines = formatCollectOrder(order, orderLinesToCollect);

  return (
    <div className="p-4">
      <Link
        to={`/collect/orders?childId=${childId}&groupName=${encodeURIComponent(
          groupName ?? ""
        )}`}
      >
        {order.collectionStatus === CollectionStatusEnum.COLLECTED ||
        activeOrderLines.length === 0 ? (
          <CollectSummary.Banner />
        ) : (
          <CollectSummary orderLines={activeOrderLines} />
        )}
      </Link>
    </div>
  );
};

function formatCollectOrder(
  order: TransformedCollectOrder,
  orderLinesToCollect: CollectStore["orderLines"]
): CollectSummaryOrderLine[] {
  const isCollected = order.collectionStatus === CollectionStatusEnum.COLLECTED;

  const linesToInclude = isCollected
    ? order.collectedOrderLines
    : order.activeOrderLines.filter((line) =>
        orderLinesToCollect.includes(line.id)
      );

  return linesToInclude.reduce<CollectSummaryOrderLine[]>((acc, line) => {
    const existing = acc.find((item) => item.name === line.productName);

    if (existing) {
      existing.quantity += 1;
    } else {
      acc.push({
        name: line.productName,
        quantity: 1
      });
    }

    return acc;
  }, []);
}
